import __imagem0 from "../../assets/marca/allingressos/logo.png";
import __imagem1 from "../../assets/marca/lmduo/logo.png";
import __imagem2 from "../../assets/marca/webtickets/logo.png";
import __imagem3 from "../../assets/marca/teamingressos/logo.png";
import __imagem4 from "../../assets/marca/dl7tickets/logo.png";
import __imagem5 from "../../assets/marca/showingresso/logo.png";
import __imagem6 from "../../assets/marca/ticketup/logo.png";
import __imagem7 from "../../assets/marca/tocpay/logo.png";
import __imagem8 from "../../assets/marca/weticket/logo.png";
import __imagem9 from "../../assets/marca/estherevent/logo.png";
import __imagem10 from "../../assets/marca/showecia/logo.png";
import __imagem11 from "../../assets/marca/pixpag/logo.png";
import __imagem12 from "../../assets/marca/estacaoge/logo.png";
import __imagem13 from "../../assets/marca/gestaobp/logo.png";
/**
 * Cadastro das ticketeiras. Cada uma vira um flavor no Android (e um scheme no
 * iOS): mesmo código, empresa fixada na compilação — o operador nunca escolhe
 * a empresa, só digita ID e PIN.
 *
 * O flavor carrega a marca inteira, não só uma cor de destaque: logo, paleta de
 * noite e paleta de dia. É o que permite `src/ui/tema.ts` e `src/telas/` não
 * terem nenhum literal de cor — trocar de empresa é mexer só aqui.
 *
 * Para adicionar uma ticketeira: acrescente a entrada abaixo, ponha o logo em
 * `assets/marca/<id>/logo.png` e crie o flavor no `android/app/build.gradle`.
 */

import type {ImageSourcePropType} from 'react-native';

export interface Ticketeira {
  /** Identificador curto — é o nome do flavor no Gradle. */
  id: string;
  nome: string;
  dominio: string;
  /** Base das chamadas REST, o mesmo `/rest/4.5/` do TMobi. */
  apiBase: string;

  /** Marca. */
  logo: ImageSourcePropType;
  /**
   * Cor da placa atrás do logo, por modo; `null` põe o logo direto no fundo.
   * Existe porque os PNGs entregues não servem nos dois modos: o da All
   * Ingressos é branco com transparência (some no tema dia) e o da LM Duo é
   * opaco com fundo branco (vira um quadrado claro no tema noite).
   */
  placaLogo: {noite: string | null; dia: string | null};

  /** Ação e destaque. */
  corPrimaria: string;
  /** Tinta do texto/ícone SOBRE corPrimaria — não é derivada, é cadastrada. */
  corTintaPrimaria: string;
  /** Destaque secundário: rótulos, dot de sync, número em evidência. */
  corDestaque: string;

  /** Superfícies — noite. */
  corFundo: string;
  corSuperficie: string;
  corSuperficieAlta: string;
  corBorda: string;
  corTexto: string;
  corTextoMedio: string;
  corTextoFraco: string;
  corTextoApagado: string;

  /** Superfícies — dia (sol forte). */
  /** Variante da primária com contraste suficiente sobre branco. */
  corPrimariaDia: string;
  corTintaPrimariaDia: string;
  corDestaqueDia: string;
  corFundoDia: string;
  corSuperficieDia: string;
  corSuperficieAltaDia: string;
  corBordaDia: string;
  corTextoDia: string;
  corTextoMedioDia: string;
  corTextoFracoDia: string;
  corTextoApagadoDia: string;
}

/**
 * Superfícies que não pertencem à marca: erro, aviso, e as duas tintas que a
 * busca ao portador usa para "aprovado" e "já validado". Ficam fora do cadastro
 * da empresa de propósito — vermelho de erro tem que ser vermelho em toda
 * ticketeira —, mas moram aqui para o tema continuar sem literais.
 */
export const SEMANTICAS = {
  noite: {
    erro: '#DC2626',
    erroFundo: '#DC262618',
    erroBorda: '#DC262655',
    erroTinta: '#FCA5A5',
    aviso: '#D97706',
    avisoFundo: '#D9770622',
    avisoBorda: '#D9770666',
    avisoTinta: '#FBBF24',
    sucessoFundo: '#00A63E22',
    sucessoTinta: '#4ADE80',
    infoFundo: '#0284C722',
    infoBorda: '#0284C766',
    infoTinta: '#7DD3FC',
    /** Placas sobre a imagem da câmera — valem nos dois modos. */
    vidroFundo: 'rgba(10,12,14,0.72)',
    vidroBorda: '#FFFFFF1F',
    vidroTexto: '#FFFFFF',
    vidroTextoFraco: '#9AA3AB',
    /** Cortina sobre a câmera, fora da janela de leitura. */
    escurecedorMira: 'rgba(0,0,0,0.42)',
    /** Fundo atrás de diálogo e folha — preto puro nos dois temas. */
    escurecedorModal: 'rgba(0,0,0,0.68)',
  },
  dia: {
    erro: '#DC2626',
    erroFundo: '#FEF2F2',
    erroBorda: '#FCA5A5',
    erroTinta: '#991B1B',
    aviso: '#D97706',
    avisoFundo: '#FFFBEB',
    avisoBorda: '#FCD34D',
    avisoTinta: '#92400E',
    sucessoFundo: '#DCFCE7',
    sucessoTinta: '#166534',
    infoFundo: '#E0F2FE',
    infoBorda: '#7DD3FC',
    infoTinta: '#075985',
    vidroFundo: 'rgba(10,12,14,0.72)',
    vidroBorda: '#FFFFFF1F',
    vidroTexto: '#FFFFFF',
    vidroTextoFraco: '#9AA3AB',
    /** Cortina sobre a câmera, fora da janela de leitura. */
    escurecedorMira: 'rgba(0,0,0,0.42)',
    /** Fundo atrás de diálogo e folha — preto puro nos dois temas. */
    escurecedorModal: 'rgba(0,0,0,0.68)',
  },
};

export const TICKETEIRAS: Record<string, Ticketeira> = {
  allingressos: {
    id: 'allingressos',
    nome: 'All Ingressos',
    dominio: 'www.allingressos.com.br',
    apiBase: 'https://www.allingressos.com.br/rest/4.5/',

    logo: {uri: __imagem0},
    // Logo branco com vermelho: sobre branco não sobra nada dele.
    placaLogo: {noite: null, dia: '#08090A'},

    corPrimaria: '#00A650',
    corTintaPrimaria: '#00190C',
    corDestaque: '#00C95F',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    corPrimariaDia: '#00873F',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#00873F',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },

  lmduo: {
    id: 'lmduo',
    nome: 'LM Duo',
    dominio: 'www.lmduo.com.br',
    apiBase: 'https://www.lmduo.com.br/rest/4.5/',

    logo: {uri: __imagem1},
    // PNG opaco de fundo branco: no escuro ele precisa da placa para não virar
    // um retângulo claro solto na tela. Uma versão transparente dispensa isso.
    placaLogo: {noite: '#FFFFFF', dia: '#FFFFFF'},

    // Azul do logo entregue, não o vermelho do cadastro antigo.
    corPrimaria: '#0B96E0',
    corTintaPrimaria: '#002337',
    corDestaque: '#35C8F5',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    corPrimariaDia: '#0A6FA8',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#0A6FA8',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },

  webtickets: {
    id: 'webtickets',
    nome: 'WebTickets',
    dominio: 'www.webtickets.com.br',
    apiBase: 'https://www.webtickets.com.br/rest/4.5/',

    logo: {uri: __imagem2},
    // Letreiro branco em PNG transparente, como o da All Ingressos: sobre o
    // branco do tema dia não sobraria nada dele.
    placaLogo: {noite: null, dia: '#08090A'},

    // Do CSS do site: --secondary é o verde de ação e --tertiary o turquesa,
    // as duas pontas do gradiente da marca. --secondaryd é a versão que eles
    // mesmos usam sobre branco, e vira a primária do tema dia.
    corPrimaria: '#1CDF63',
    corTintaPrimaria: '#04240F',
    corDestaque: '#00DABF',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    corPrimariaDia: '#087E3D',
    corTintaPrimariaDia: '#FFFFFF',
    // O turquesa da marca escurecido: sobre branco o #00DABF não se lê.
    corDestaqueDia: '#00776A',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },
  teamingressos: {
    id: 'teamingressos',
    nome: 'Team Ingressos',
    dominio: 'www.teamingressos.com.br',
    apiBase: 'https://www.teamingressos.com.br/rest/4.5/',

    logo: {uri: __imagem3},
    // "TEAM" é desenhado em contorno branco: sobre o branco do tema dia
    // sobraria só o "INGRESSOS" azul e a marca ficaria pela metade.
    placaLogo: {noite: null, dia: '#08090A'},

    // Azul tirado do próprio logo — é o único sinal de marca que este lote
    // tem. O CSS do site é o tema padrão da plataforma, igual ao das outras
    // ticketeiras que rodam nela, e por isso não serve de referência aqui.
    corPrimaria: '#0091DC',
    corTintaPrimaria: '#00202F',
    corDestaque: '#33ACEA',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    corPrimariaDia: '#0072AD',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#0072AD',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },
  dl7tickets: {
    id: 'dl7tickets',
    nome: 'DL7 Tickets',
    dominio: 'www.dl7tickets.com.br',
    apiBase: 'https://www.dl7tickets.com.br/rest/4.5/',

    logo: {uri: __imagem4},
    // "DL7" é branco dentro do colchete ciano: sobre branco some a marca e
    // sobra só o ".tickets". A placa usa o quase-preto do favicon deles.
    placaLogo: {noite: null, dia: '#091219'},

    // Ciano tirado do logo, confirmado pelo favicon — o CSS do site é o tema
    // padrão da plataforma e não diz nada sobre este lote.
    corPrimaria: '#42BFE7',
    corTintaPrimaria: '#04222C',
    corDestaque: '#6FD3F0',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    // O ciano da marca sobre branco fica ilegível; esta é a versão escurecida
    // até o contraste que um botão com tinta branca exige.
    corPrimariaDia: '#0F7CA0',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#0F7CA0',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },
  showingresso: {
    id: 'showingresso',
    nome: 'Show Ingresso',
    dominio: 'www.showingresso.com.br',
    apiBase: 'https://www.showingresso.com.br/rest/4.5/',

    logo: {uri: __imagem5},
    // PNG opaco com fundo #F7F7F7, como o da LM Duo. A placa usa esse mesmo
    // tom nos dois temas para não sobrar costura em volta da arte.
    placaLogo: {noite: '#F7F7F7', dia: '#F7F7F7'},

    corPrimaria: '#FF6600',
    // Laranja puro pede tinta escura: branco em cima dele não alcança
    // contraste de leitura.
    corTintaPrimaria: '#2B1000',
    corDestaque: '#FF8534',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    // Sobre branco o laranja da marca não se lê; esta é a versão aprofundada
    // que ainda é laranja e aguenta tinta branca por cima.
    corPrimariaDia: '#C24E00',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#C24E00',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },
  ticketup: {
    id: 'ticketup',
    nome: 'Ticket Up',
    // Só o painel serve a API: www.ticketup.com.br redireciona e não responde
    // em /rest/4.5/. Não siga aqui o padrão "www" das outras.
    dominio: 'painel.ticketup.com.br',
    apiBase: 'https://painel.ticketup.com.br/rest/4.5/',

    logo: {uri: __imagem6},
    // Placa ao contrário das outras: metade do logo é o navy #101820, que
    // sumiria no fundo escuro. Aqui quem precisa de placa é o tema noite; no
    // dia o branco da tela já é o fundo certo para a marca.
    placaLogo: {noite: '#FFFFFF', dia: null},

    // O dourado da marca é escuro demais para virar botão no tema noite —
    // clareado aqui e mantido no original no tema dia, onde ele funciona.
    corPrimaria: '#C4AD5A',
    corTintaPrimaria: '#1A1608',
    corDestaque: '#DCC470',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    corPrimariaDia: '#736635',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#736635',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },

  tocpay: {
    id: 'tocpay',
    nome: 'TocPay',
    // A bilheteria fica num subdomínio próprio, não no site institucional:
    // tocpaysolucoes.com é a empresa, ingressos.* é quem responde /rest/4.5/.
    dominio: 'ingressos.tocpaysolucoes.com',
    apiBase: 'https://ingressos.tocpaysolucoes.com/rest/4.5/',

    logo: {uri: __imagem7},
    // O wordmark é preto sobre transparência: sem placa ele desaparece no tema
    // noite. No dia o branco da tela já serve de fundo à marca.
    placaLogo: {noite: '#FFFFFF', dia: null},

    /**
     * A marca é vermelha (#F90016), e o vermelho não sobe para a interface: ele
     * é a cor de "já validado" (`SEMANTICAS.erro`), e um botão vermelho tira o
     * susto da tela de recusa. É a mesma escolha da All Ingressos, cujo logo
     * também é vermelho e cuja ação é verde.
     *
     * Teal porque as outras já ocupam três azuis e dois verdes — e porque ele
     * convive com o vermelho do logo sem disputar atenção com ele.
     */
    corPrimaria: '#00B5AD',
    corTintaPrimaria: '#04211F',
    corDestaque: '#3AD3CB',

    corFundo: '#08090A',
    corSuperficie: '#14171A',
    corSuperficieAlta: '#1C2024',
    corBorda: '#262B31',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B7C0C8',
    corTextoFraco: '#8A939C',
    corTextoApagado: '#4A525A',

    // O teal do tema noite não tem contraste sobre branco: escurecido para o
    // dia, como a Ticket Up faz com o dourado dela.
    corPrimariaDia: '#00706B',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#00706B',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6F8',
    corSuperficieAltaDia: '#E9EDF1',
    corBordaDia: '#D3D9E0',
    corTextoDia: '#0A0C0E',
    corTextoMedioDia: '#3F474F',
    corTextoFracoDia: '#59626B',
    corTextoApagadoDia: '#9AA3AB',
  },

  /**
   * CUIDADO ao mexer aqui: `weticket` e `webtickets` são empresas diferentes,
   * com pacotes diferentes (br.com.weticket / br.com.webtickets). Os ids se
   * distinguem por um "b" e um "s" — confira o alvo antes de compilar ou de
   * subir qualquer coisa.
   */
  weticket: {
    id: 'weticket',
    nome: 'We Ticket',
    dominio: 'weticket.com.br',
    apiBase: 'https://weticket.com.br/rest/4.5/',

    logo: {uri: __imagem8},
    // Assinatura empilhada: o "we ticket" embaixo é quase preto sobre
    // transparência e sumiria no tema noite. De dia a tela branca já basta.
    placaLogo: {noite: '#FFFFFF', dia: null},

    // O símbolo é um degradê de rosa (#CE5070) a violeta (#601BDA); o roxo do
    // meio é o que representa a marca numa cor só. Clareado para virar botão
    // legível no fundo escuro, e escurecido no tema dia para ter contraste
    // sobre branco.
    corPrimaria: '#A34BE8',
    corTintaPrimaria: '#FFFFFF',
    corDestaque: '#C77BF0',

    corFundo: '#08090A',
    corSuperficie: '#15141A',
    corSuperficieAlta: '#1D1B24',
    corBorda: '#2A2732',
    corTexto: '#FFFFFF',
    corTextoMedio: '#BEB8C8',
    corTextoFraco: '#8F889C',
    corTextoApagado: '#4E4859',

    corPrimariaDia: '#6B10A0',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#6B10A0',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F6F4F8',
    corSuperficieAltaDia: '#EBE8F1',
    corBordaDia: '#D8D3E0',
    corTextoDia: '#0C0A0E',
    corTextoMedioDia: '#443F4F',
    corTextoFracoDia: '#5E586B',
    corTextoApagadoDia: '#9E98AB',
  },

  estherevent: {
    id: 'estherevent',
    nome: 'Esther Event',
    dominio: 'estherevent.com.br',
    apiBase: 'https://estherevent.com.br/rest/4.5/',

    logo: {uri: __imagem9},
    // Ao contrário da maioria: o wordmark desta marca é BRANCO, então quem
    // precisa de placa é o tema dia — de noite o fundo escuro já é o lugar
    // dela. Mesmo caso da All Ingressos.
    placaLogo: {noite: null, dia: '#08090A'},

    // O azul da marca (#2E78EE) já tem contraste no escuro e vira a ação
    // direto. É mais índigo que os azuis da LM Duo e da Team Ingressos, que
    // puxam para o ciano.
    corPrimaria: '#2E78EE',
    corTintaPrimaria: '#FFFFFF',
    corDestaque: '#63A0FF',

    corFundo: '#08090A',
    corSuperficie: '#13161B',
    corSuperficieAlta: '#1B1F26',
    corBorda: '#262C35',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B6BECA',
    corTextoFraco: '#89929E',
    corTextoApagado: '#49515C',

    corPrimariaDia: '#1B57BC',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#1B57BC',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F3F6FA',
    corSuperficieAltaDia: '#E7EDF5',
    corBordaDia: '#D1DAE5',
    corTextoDia: '#090C10',
    corTextoMedioDia: '#3E4650',
    corTextoFracoDia: '#58616C',
    corTextoApagadoDia: '#99A2AD',
  },

  showecia: {
    id: 'showecia',
    nome: 'Show e Cia',
    dominio: 'showecia.com.br',
    apiBase: 'https://showecia.com.br/rest/4.5/',

    logo: {uri: __imagem10},
    // O PNG entregue vem com fundo branco opaco, não transparente: no tema
    // noite ele viraria um retângulo claro solto na tela. A placa branca faz
    // disso uma decisão em vez de um acidente. Mesmo caso da LM Duo.
    placaLogo: {noite: '#FFFFFF', dia: null},

    /**
     * A marca é navy (#091931) com laranja (#FE7008). O navy não vira ação:
     * some no fundo escuro. Sobra o laranja, que é o mesmo caminho da Show
     * Ingresso — e sim, as duas ficam com laranjas quase idênticos
     * (#FE7008 aqui, #FF6600 lá). São empresas diferentes e apps separados;
     * quem vê uma nunca vê a outra. Só não confunda ao olhar screenshot.
     *
     * O navy aparece nas superfícies, que é onde ele funciona.
     */
    corPrimaria: '#FE7008',
    corTintaPrimaria: '#1A0A00',
    corDestaque: '#FF9440',

    corFundo: '#070B12',
    corSuperficie: '#101725',
    corSuperficieAlta: '#182132',
    corBorda: '#222E42',
    corTexto: '#FFFFFF',
    corTextoMedio: '#B4BECD',
    corTextoFraco: '#8792A3',
    corTextoApagado: '#485468',

    corPrimariaDia: '#A84600',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#A84600',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F4F6FA',
    corSuperficieAltaDia: '#E8EDF4',
    corBordaDia: '#D2D9E4',
    corTextoDia: '#080B11',
    corTextoMedioDia: '#3D4553',
    corTextoFracoDia: '#57606F',
    corTextoApagadoDia: '#98A1AF',
  },

  pixpag: {
    id: 'pixpag',
    nome: 'PixPag',
    // A bilheteria mora no subdomínio `sistema`, e o domínio é .net: não siga
    // aqui o padrão "www.<nome>.com.br" das outras.
    dominio: 'sistema.pixpag.net',
    apiBase: 'https://sistema.pixpag.net/rest/4.5/',

    logo: {uri: __imagem11},
    // Marca em cima, "pixpag" embaixo — o wordmark é vermelho sobre
    // transparência e se vira nos dois modos, mas no dia o vermelho sobre
    // branco fica agressivo; a placa clara do dia é dispensada e a de noite
    // também. Fica direto no fundo, como o cadastro permite.
    placaLogo: {noite: null, dia: null},

    /**
     * A marca vai de âmbar (#E5810A) a vermelho (#C61414). A ação é o âmbar:
     * o vermelho é a cor de "já validado", e usá-lo em botão apagaria o susto
     * da tela de recusa — mesma decisão da tocpay e da All Ingressos.
     *
     * Fica sendo o terceiro laranja da carteira, junto de Show Ingresso e
     * Show e Cia. Puxa mais para o dourado que os dois, mas em captura de tela
     * as três se parecem.
     */
    corPrimaria: '#E5810A',
    corTintaPrimaria: '#1F1200',
    corDestaque: '#F2A63C',

    corFundo: '#0A0806',
    corSuperficie: '#171310',
    corSuperficieAlta: '#211B16',
    corBorda: '#2E251E',
    corTexto: '#FFFFFF',
    corTextoMedio: '#C6BCB2',
    corTextoFraco: '#968C82',
    corTextoApagado: '#544C44',

    corPrimariaDia: '#8A4D00',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#8A4D00',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F8F5F2',
    corSuperficieAltaDia: '#EFEAE4',
    corBordaDia: '#DED6CD',
    corTextoDia: '#100C08',
    corTextoMedioDia: '#4A4239',
    corTextoFracoDia: '#655C52',
    corTextoApagadoDia: '#A69C92',
  },

  estacaoge: {
    id: 'estacaoge',
    nome: 'Estação Gê',
    // COM "www": estacaoge.com puro não resolve (a conexão nem abre). É a
    // segunda exceção ao padrão, junto do painel.ticketup.com.br.
    dominio: 'www.estacaoge.com',
    apiBase: 'https://www.estacaoge.com/rest/4.5/',

    logo: {uri: __imagem12},
    // O logo é um quadrado com o próprio navy dentro, sem transparência. No
    // tema noite ele encosta no fundo e a borda some sozinha — por isso nada
    // de placa. No dia, o quadrado escuro sobre branco é a marca, e é assim
    // que ela existe.
    placaLogo: {noite: null, dia: null},

    /**
     * Paleta informada pela própria ticketeira, não deduzida do logo:
     * #131F3A fundo, #1B2A47 variação, #F4D6A8 destaque, #F8DEB2 claro,
     * #0F192F contraste.
     *
     * A ação é o bege sobre o navy — o inverso das outras onze, que têm cor
     * viva sobre fundo escuro. Funciona porque o contraste é altíssimo, e é o
     * que a marca faz no material dela.
     */
    corPrimaria: '#F4D6A8',
    corTintaPrimaria: '#131F3A',
    corDestaque: '#F8DEB2',

    corFundo: '#131F3A',
    corSuperficie: '#1B2A47',
    corSuperficieAlta: '#233457',
    corBorda: '#2E4066',
    corTexto: '#FFFFFF',
    corTextoMedio: '#C3CCE0',
    corTextoFraco: '#94A0BC',
    corTextoApagado: '#5A688A',

    // De dia o bege não tem contraste sobre branco: a ação vira o navy, com o
    // bege como tinta — a mesma dupla, trocada de lado.
    corPrimariaDia: '#131F3A',
    corTintaPrimariaDia: '#F4D6A8',
    corDestaqueDia: '#1B2A47',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F3F5F9',
    corSuperficieAltaDia: '#E7EBF3',
    corBordaDia: '#D0D7E4',
    corTextoDia: '#0F192F',
    corTextoMedioDia: '#3C465C',
    corTextoFracoDia: '#566178',
    corTextoApagadoDia: '#97A0B4',
  },

  gestaobp: {
    id: 'gestaobp',
    // O site se apresenta como GESTAOBP e a marca do logo é "Black Pass" — são
    // a mesma empresa. Fica o nome do domínio, que é como o cliente a chama.
    nome: 'Gestão BP',
    dominio: 'www.gestaobp.com.br',
    apiBase: 'https://www.gestaobp.com.br/rest/4.5/',

    logo: {uri: __imagem13},
    // O logo "Black Pass" vem com fundo branco chapado: no tema noite viraria
    // um retângulo claro solto. Placa branca de noite, nada de dia.
    placaLogo: {noite: '#FFFFFF', dia: null},

    // Verde neon da marca, amostrado do ícone do validador que a empresa já
    // usa. É mais elétrico que os verdes da WebTickets (#1CDF63) e da All
    // Ingressos (#00A650), e pede tinta escura por cima.
    corPrimaria: '#02F84F',
    corTintaPrimaria: '#08130C',
    corDestaque: '#5BFF8E',

    // Cinzas neutros, do próprio fundo do ícone (#1C1C1C) para baixo: a marca
    // não tem matiz nas superfícies, só o verde em cima delas.
    corFundo: '#0B0B0B',
    corSuperficie: '#161616',
    corSuperficieAlta: '#1F1F1F',
    corBorda: '#2B2B2B',
    corTexto: '#FFFFFF',
    corTextoMedio: '#BDBDBD',
    corTextoFraco: '#8E8E8E',
    corTextoApagado: '#505050',

    // O neon não tem contraste sobre branco: de dia a ação vira um verde
    // fechado, com tinta branca.
    corPrimariaDia: '#046B26',
    corTintaPrimariaDia: '#FFFFFF',
    corDestaqueDia: '#046B26',
    corFundoDia: '#FFFFFF',
    corSuperficieDia: '#F5F6F5',
    corSuperficieAltaDia: '#EAECEA',
    corBordaDia: '#D6D9D6',
    corTextoDia: '#0A0C0A',
    corTextoMedioDia: '#414441',
    corTextoFracoDia: '#5B5F5B',
    corTextoApagadoDia: '#9BA09B',
  },
};

export const TICKETEIRA_PADRAO = 'allingressos';
